import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Row, Col, Button, message, DatePicker, TimePicker, Select, Upload } from 'antd';
import DeliveryNoteTable from './DeliveryNoteTable';
import { CloseOutlined, ExclamationCircleOutlined, CheckCircleOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import './AddToSignedDeliveryNotePopup.less';
import axios from 'axios';
import { baseURL } from '../../../../globalUtils/axiosConfig';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAllLocations } from '../../../LocationManager/redux/actions';
import pdfIcon from '../../../../assets/c9785d49e2c04999be9f5d27fad98100.png';

const AddToSignedDeliveryNotePopup = ({ visible, onClose, project_id, selectedItems = {}, selectedDeliveryNoteType }) => {
  const { user } = useSelector((state) => state.authReducer);
  const { allLocations, fetchLocationsLoading } = useSelector((state) => state.locationsReducer || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const getCurrentUserName = () => {
    if (!user) return 'Current User';
    
    if (user.first_name && user.surname) {
      return `${user.first_name} ${user.surname}`;
    }
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.name) {
      return user.name;
    }
    if (user.username) {
      return user.username;
    }
    if (user.email) {
      return user.email;
    }
    
    return 'Current User';
  };
  
  const [formData, setFormData] = useState({
    to: '',
    attention: '',
    regarding: '',
    truckTrailerReg: '',
    driverName: '',
    driverArrivedAt: null,
    driverArrivedTime: null,
    receiverName: '',
    receivedAt: null,
    receivedAtTime: null,
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemsData, setSelectedItemsData] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deliveryNoteResponse, setDeliveryNoteResponse] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const confirmationRef = useRef(null);
  
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Format numbers with comma as decimal separator and limit to one decimal place
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0,0';
    
    // Convert to number to handle any string inputs
    const num = Number(value);
    
    // Check if it's a whole number
    if (Number.isInteger(num)) {
      // For whole numbers, add ',0' at the end
      return `${num},0`;
    } else {
      // For decimal numbers, keep only one decimal place and replace '.' with ','
      const formatted = num.toFixed(1);
      return formatted.replace('.', ',');
    }
  };
  
  // Format percentage values with comma as decimal separator
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0,00%';
    
    // Convert to number to handle any string inputs
    const num = Number(value);
    
    // For whole numbers with 3 digits or less (like 100), add ',00'
    if (Number.isInteger(num) && num.toString().length <= 3) {
      return `${num},00%`;
    } else {
      // For decimal numbers, keep two decimal places and replace '.' with ','
      const formatted = num.toFixed(2);
      return `${formatted.replace('.', ',')}%`;
    }
  };

  // Fetch locations when component mounts
  useEffect(() => {
    dispatch(fetchAllLocations({
     managedProjectId: project_id,
    }));
  }, [dispatch]);

  // Process the selected items when they change
  useEffect(() => {
    // Check if we have any selected items
    const materialTypes = Object.keys(selectedItems);
    if (materialTypes.length === 0) {
      // If no items are selected, use sample data for testing
      setTableData();
      return;
    }

    setLoading(true);
    try {
      // Extract all items from all material types
      const allItemsData = [];
      
      materialTypes.forEach(materialType => {
        if (selectedItems[materialType] && selectedItems[materialType].items) {
          // Get all items for this material type
          const itemsInType = Object.values(selectedItems[materialType].items);
          
          // Add material type info to each item
          const itemsWithType = itemsInType.map(item => ({
            ...item,
            materialType,
            uom: selectedItems[materialType].uom || ''
          }));
          
          allItemsData.push(...itemsWithType);
        }
      });
      
      // Store the selected items data
      setSelectedItemsData(allItemsData);
      
      // Transform the data into the format expected by DeliveryNoteTable
      transformDataForTable(allItemsData);
    } catch (error) {
      console.error('Error processing selected items data:', error);
      message.error('Failed to process selected items data');
    } finally {
      setLoading(false);
    }
  }, [selectedItems]);
  
  // Transform the data into the format expected by DeliveryNoteTable
  const transformDataForTable = (itemsData) => {
    if (!itemsData || itemsData.length === 0) {
      setTableData(selectedItems);
      return;
    }
    
    // Group items by category
    const groupedByCategory = {};
    
    itemsData.forEach(item => {
      const category = item.type?.toLowerCase() === 'bolts' ? 'BOLTS' : 'MATERIAL';
      const uom = item.type?.toLowerCase() === 'bolts' ? 'pcs' : 'kg';
      
      if (!groupedByCategory[category]) {
        groupedByCategory[category] = {
          items: [],
          totalQuantity: 0,
          totalGross: 0,
          uom
        };
      }
      
      // Use the quantity and total from the item data
      const quantity = item.qty || 0;
      const totalGross = item.totalGross || 0;
      
      // Add to category totals
      groupedByCategory[category].totalQuantity += quantity;
      groupedByCategory[category].totalGross += totalGross;
      
      // Add item to the category
      groupedByCategory[category].items.push({
        id: item.id,
        item: groupedByCategory[category].items.length + 1, // Sequential item number within category
        model_no: item.model || '',
        drawing_mark_no: item.markNo || '',
        description: item.description || '',
        quantity,
        total_gross: totalGross,
        uom,
        category
      });
    });
    
    // Convert the grouped data into the format expected by DeliveryNoteTable
    const tableItems = [];
    let itemId = 1;
    
    Object.keys(groupedByCategory).forEach(category => {
      const categoryData = groupedByCategory[category];
      const uom = categoryData.uom;
      
      // Add category header
      tableItems.push({
        id: itemId++,
        category: `${category} (${uom})`,
        isCategory: true
      });
      
      // Add category items
      categoryData.items.forEach(item => {
        tableItems.push({
          ...item,
          id: itemId++
        });
      });
      
      // Add category total
      tableItems.push({
        id: itemId++,
        category,
        isTotal: true,
        quantity: categoryData.totalQuantity,
        total_gross: categoryData.totalGross,
        uom
      });
    });
    
    setTableData(tableItems);
  };

  const handleAttachClick = () => {
    // Validate that at least one file is uploaded
    if (!uploadedFiles || uploadedFiles.length === 0) {
      message.error('Please upload at least one signed delivery note file');
      return;
    }
    
    // Show inline confirmation
    setShowConfirmation(true);
  };

  // Auto-scroll to confirmation alert when it appears
  useEffect(() => {
    if (showConfirmation && confirmationRef.current) {
      // Use setTimeout to ensure the DOM has been updated
      setTimeout(() => {
        confirmationRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [showConfirmation]);

  const handleConfirmGenerate = async () => {
    setLoading(true);
    setShowConfirmation(false); // Hide confirmation immediately for better UX
    
    try {
      // Get current date in YYYY-MM-DD format
      const today = new Date();
      const currentDate = today.getFullYear() + '-' + 
        String(today.getMonth() + 1).padStart(2, '0') + '-' + 
        String(today.getDate()).padStart(2, '0');
      
      // Prepare FormData for bulk update API
      const apiFormData = new FormData();
      
      // Required fields
      if (selectedDeliveryNoteType === 'signedDeliveryNoteCorrisonProtection') {
        apiFormData.append('type', 'corrosion_protection');
      } else if (selectedDeliveryNoteType === 'signedDeliveryToSite') {
        apiFormData.append('type', 'delivery_to_site');
      }else if (selectedDeliveryNoteType === 'signedSiteReceipt') {
        apiFormData.append('type', 'site_receipt');
      }
      apiFormData.append('projectId', project_id);
      
      // Prepare progress steps data array
      const progressStepsData = [];
      
      // Extract all items from the grouped structure
      Object.keys(selectedItems).forEach(materialType => {
        if (selectedItems[materialType] && selectedItems[materialType].items) {
          Object.values(selectedItems[materialType].items).forEach(item => {
            progressStepsData.push({
              uniqueId: item.uniqueId,
              status: "received",
              notes: "Signed delivery note attached",
              receivedAt: currentDate, // Current date in YYYY-MM-DD format
              receivedQty: Number(item.qty),
              receivedLocationId: Number(selectedLocation),
              receivedById: user?.id,
              createdById: user?.id,
              materialControlRegisterId: item.id,
            });
          });
        }
      });
      
      // Append the data array as JSON string
      apiFormData.append('data', JSON.stringify(progressStepsData));
      
      // Append uploaded files
      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          if (file.originFileObj) {
            apiFormData.append('documents', file.originFileObj);
          }
        });
      }
      
      // Make the PATCH API call to bulk update endpoint
      const response = await axios.patch(
        `${baseURL}/material-control-registers/steps/bulk-update`,
        apiFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.status === 200 || response.status === 201) {
        message.success('Signed delivery note attached successfully');
        setDeliveryNoteResponse(response.data);
        setShowSuccess(true);
        // Redirect to delivery notes page for this project
        // navigate(`/project/delivery-notes/${project_id}`);
      } else {
        throw new Error('Failed to attach signed delivery note');
      }
      
    } catch (error) {
      console.error('Error attaching signed delivery note:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 'Failed to attach signed delivery note';
        message.error(errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        message.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  // Handle file upload
  const handleFileUpload = ({ file, onSuccess, onError }) => {
    setUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      try {
        const newFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: URL.createObjectURL(file),
          originFileObj: file
        };
        
        setUploadedFiles(prev => [...prev, newFile]);
        onSuccess(newFile);
        message.success(`${file.name} uploaded successfully`);
      } catch (error) {
        onError(error);
        message.error(`${file.name} upload failed`);
      } finally {
        setUploading(false);
      }
    }, 1000);
  };

  // Handle file removal
  const handleFileRemove = (file) => {
    setUploadedFiles(prev => prev.filter(f => f.uid !== file.uid));
    message.success(`${file.name} removed successfully`);
  };

  const handleClose = () => {
    if (showSuccess) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  const handleOpenPDF = () => {
    if (deliveryNoteResponse && deliveryNoteResponse.attachments && deliveryNoteResponse.attachments.length > 0) {
      const pdfAttachment = deliveryNoteResponse.attachments.find(attachment => 
        attachment.mimetype === 'application/pdf'
      );
      
      if (pdfAttachment) {
        // Open PDF in new tab using the file path from the API response
        const pdfUrl = `${baseURL}/${pdfAttachment.path}`;
        window.open(pdfUrl, '_blank');
      }
    }
  };


  return (
    <Modal
      title={
        <div className="delivery-note-popup-header">
          <span className="delivery-note-title">SIGNED DELIVERY NOTE</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1123}
      className="delivery-note-popup"
      maskClosable={false}
      centered
    >
      {showSuccess ? (
        <div className="delivery-note-content">
        <div className="success-content">
          <p className="success-message" style={{ marginLeft: '30px', fontSize: '12px' }}>The Signed Delivery Note has been attached successfully.</p>
          
          <div className="pdf-download-section">
            <img src={pdfIcon} alt="PDF" className="pdf-icon" style={{ marginLeft: '30px', width: '26px', height: '32px' }} />
            <span 
              className="pdf-filename" 
              style={{ 
                marginLeft: '10px', 
                fontSize: '12px',
                color: '#030508',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={handleOpenPDF}
            >
              {deliveryNoteResponse ? 
                `${deliveryNoteResponse.date ? new Date(deliveryNoteResponse.date).toLocaleDateString('en-CA') : 'signed-delivery-note'} - ${deliveryNoteResponse.delNoteNo || 'document'}.pdf` : 
                'signed-delivery-note.pdf'
              }
            </span>
          </div>
          
          <div className="horizontal-divider"></div>
          
          <div className="success-actions" style={{ display: 'flex', justifyContent: 'center' }}>
            <Button 
              onClick={handleClose}
              className="delivery-register-button"
              style={{
                border: '1px solid #f25003',
                color: '#f25003',
                backgroundColor: 'white',
                marginBottom: '30px'
              }}
            >
              Done
            </Button>
          </div>
        </div>
        </div>
      ) : (
        <div className="delivery-note-content">

        <Row gutter={[24, 16]} className="form-row">
          <Col span={12}>
            <div className="form-group inline left-aligned">
              <label className="title-text">Add Signed Delivery Note to the following items:</label>
            </div>
          </Col>
        </Row>
        <DeliveryNoteTable progressData={selectedItems} loading={loading} />
        <div className="horizontal-divider-below"></div>

        {/* Signature Boxes */}
        <div className="signed-signature-boxes-container">
          {/* Location Dropdown */}
          <div className="location-section">
            <div className="location-container-centered">
              <label className="form-label">LOCATION:</label>
              <Select
                placeholder="Select Location"
                value={selectedLocation}
                onChange={setSelectedLocation}
                loading={fetchLocationsLoading}
                className="location-dropdown"
                style={{ width: '200px', marginLeft: '10px' }}
              >
                {allLocations && allLocations.map(location => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="upload-section">
            <div className="upload-container">
              <div className="upload-header">Upload Signed Delivery Note</div>
              <Upload
                customRequest={handleFileUpload}
                onRemove={handleFileRemove}
                fileList={uploadedFiles}
                accept=".pdf,.jpg,.jpeg,.png"
                className="signature-uploader"
              >
                <Button 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  className="upload-button"
                >
                  UPLOAD FILE
                </Button>
              </Upload>
            </div>
          </div>
        </div>

        <div className="footer-actions">
          <Button 
            type="primary" 
            onClick={handleAttachClick}
            className="submit-button"
            disabled={showConfirmation}
          >
            Attach Signed Delivery Note
          </Button>
          
          {showConfirmation && (
            <div className="inline-confirmation" ref={confirmationRef}>
              <div className="confirmation-message">
                <ExclamationCircleOutlined className="confirmation-icon" />
                <div className="confirmation-text">
                  <p>Are you sure you want to attach the Signed Delivery Note to these items?</p>
                  <p className="confirmation-subtext">Please note this action cannot be undone.</p>
                </div>
              </div>
              <div className="confirmation-actions">
                <Button 
                  type="primary" 
                  onClick={handleConfirmGenerate}
                  loading={loading}
                  className="confirm-yes-button"
                >
                  Yes
                </Button>
                <Button 
                  onClick={handleCancelConfirmation}
                  className="confirm-no-button"
                >
                  No
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

    </Modal>
  );
};

export default AddToSignedDeliveryNotePopup;
